import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import TenantsAPI from '../../namespace/api/tenants';
import apiClient from './apiClient';

export interface LogEvent {
	timestamp?: string;
	message: string;
	requestId?: string;
}

export interface ScriptLogResponse {
	logEvents: LogEvent[];
	nextPosition: string;
}

export interface ScriptLogRequest {
	requestId?: string;
	minTimestamp?: number;
	maxTimestamp?: number;
	oldestComeFirst?: boolean;
	position?: string;
	searchPhrase?: string;
	limit?: number;
}

const getScriptLogs = async (
	request: ScriptLogRequest,
	tenantLocator: string,
	authToken: string,
) => {
	const res = await apiClient.post<ScriptLogResponse>(
		`/scripts/queryLogs?tenantLocator=${tenantLocator}`,
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);

	return res.data;
};

// Queries
const useScriptLogs = (
	queryKey: string[],
	request: ScriptLogRequest,
	tenantLocator: string,
	token: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			const response = await getScriptLogs(request, tenantLocator, token);
			return response;
		},
	});
};

const usePaginatedScriptLogs = (
	queryKey: string[],
	request: ScriptLogRequest,
	tenantLocator: string,
	token?: string,
	hostname?: string,
	limit?: number,
) => {
	return useInfiniteQuery(
		queryKey,
		async ({ pageParam = '' }) => {
			const response = hostname
				? await TenantsAPI.getScriptLogs(
						{ ...request, position: pageParam, limit: limit || 25 },
						hostname,
				  )
				: await getScriptLogs(
						{ ...request, position: pageParam, limit: limit || 25 },
						tenantLocator,
						token,
				  );

			return response;
		},
		{
			getPreviousPageParam: (firstPage) =>
				firstPage.nextPosition === '' ? undefined : firstPage.nextPosition,
			getNextPageParam: (lastPage) =>
				lastPage.nextPosition === '' ? undefined : lastPage.nextPosition,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		},
	);
};
const ScriptLogsAPI = {
	getScriptLogs,
	useScriptLogs,
	usePaginatedScriptLogs,
};

export default ScriptLogsAPI;
